import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type AuditEntry, type SecurityEvent, type User } from '../api.ts';
import { can, useMe } from '../auth.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Loading,
  PageHeader,
  Select,
  Table,
} from '../components/ui.tsx';

const PAGE = 50;

function Pager({
  offset,
  count,
  onChange,
}: {
  offset: number;
  count: number;
  onChange: (o: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      <Button
        variant="secondary"
        disabled={offset === 0}
        onClick={() => onChange(Math.max(0, offset - PAGE))}
      >
        {t('common.previous')}
      </Button>
      <Button variant="secondary" disabled={count < PAGE} onClick={() => onChange(offset + PAGE)}>
        {t('common.next')}
      </Button>
    </div>
  );
}

/** Mostra o que mudou de forma compacta: campo, valor antigo e valor novo. */
function Changes({ entry }: { entry: AuditEntry }) {
  const fields =
    entry.changedFields ??
    Object.keys(entry.newData ?? entry.oldData ?? {}).filter(
      (k) => k !== 'created_at' && k !== 'updated_at',
    );
  const show = (v: unknown) =>
    v === null || v === undefined ? '∅' : typeof v === 'object' ? JSON.stringify(v) : String(v);
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-muted">{fields.join(', ')}</summary>
      <dl className="mt-1 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-0.5 font-mono">
        {fields.map((f) => (
          <div key={f} className="contents">
            <dt className="text-muted">{f}</dt>
            <dd className="break-all">
              {entry.action === 'UPDATE' ? (
                <>
                  <span className="text-danger line-through">{show(entry.oldData?.[f])}</span> →{' '}
                  {show(entry.newData?.[f])}
                </>
              ) : (
                show((entry.newData ?? entry.oldData)?.[f])
              )}
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

export function AuditPage() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  // Cada aba tem a sua permissão (ADR 0042): alterações administrativas e eventos de segurança.
  const canLog = can(me, 'audit.log.read');
  const canEvents = can(me, 'audit.access.read');
  const [chosen, setTab] = useState<'log' | 'security'>('log');
  const tab = chosen === 'log' && !canLog ? 'security' : chosen;
  const [table, setTable] = useState('');
  const [offset, setOffset] = useState(0);

  // Nomes das pessoas, quando quem consulta também pode ver usuários.
  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<User[]>('/iam/users'),
    enabled: can(me, 'iam.user.read'),
  });
  const nameOf = (id: string | null) =>
    id === null
      ? t('audit.system')
      : (users.data?.find((u) => u.id === id)?.displayName ?? id.slice(0, 8));

  // Só os cadastros administrativos aparecem no histórico geral; o servidor diz quais são.
  const tables = useQuery({
    queryKey: ['audit-tables'],
    queryFn: () => api.get<string[]>('/audit/tables'),
    enabled: canLog,
  });
  const log = useQuery({
    queryKey: ['audit-log', table, offset],
    enabled: tab === 'log' && canLog,
    queryFn: () => {
      const q = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
      if (table) q.set('table', table);
      return api.get<AuditEntry[]>(`/audit/log?${q}`);
    },
  });
  const events = useQuery({
    queryKey: ['security-events', offset],
    enabled: tab === 'security' && canEvents,
    queryFn: () =>
      api.get<SecurityEvent[]>(`/audit/security-events?limit=${PAGE}&offset=${offset}`),
  });

  const dateTime = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'medium',
  });
  const switchTab = (next: 'log' | 'security') => {
    setTab(next);
    setOffset(0);
  };

  return (
    <>
      <PageHeader title={t('audit.title')} description={t('audit.description')} />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex gap-2" role="tablist">
          {canLog && (
            <Button
              role="tab"
              aria-selected={tab === 'log'}
              variant={tab === 'log' ? 'primary' : 'secondary'}
              onClick={() => switchTab('log')}
            >
              {t('audit.changes')}
            </Button>
          )}
          {canEvents && (
            <Button
              role="tab"
              aria-selected={tab === 'security'}
              variant={tab === 'security' ? 'primary' : 'secondary'}
              onClick={() => switchTab('security')}
            >
              {t('audit.security')}
            </Button>
          )}
        </div>
        {tab === 'log' && canLog && (
          <Field label={t('audit.table')}>
            <Select
              id="audit-table"
              value={table}
              onChange={(ev) => {
                setTable(ev.target.value);
                setOffset(0);
              }}
            >
              <option value="">{t('common.all')}</option>
              {(tables.data ?? []).map((name) => (
                <option key={name} value={name}>
                  {t(`audit.tables.${name}`, { defaultValue: name })}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>

      {tab === 'log' &&
        canLog &&
        (log.isPending ? (
          <Loading />
        ) : log.isError ? (
          <ErrorBox error={log.error} />
        ) : (
          <>
            <Table
              head={[
                t('audit.when'),
                t('audit.who'),
                t('audit.what'),
                t('audit.action'),
                t('audit.details'),
              ]}
            >
              {log.data.length === 0 && <EmptyRow colSpan={5}>{t('audit.empty')}</EmptyRow>}
              {log.data.map((e) => (
                <tr key={e.id}>
                  <td className="whitespace-nowrap font-mono text-xs">
                    {dateTime.format(new Date(e.occurredAt))}
                  </td>
                  <td className="text-xs">{nameOf(e.actorUserId)}</td>
                  <td className="text-xs">
                    {t(`audit.tables.${e.tableSchema}.${e.tableName}`, {
                      defaultValue: `${e.tableSchema}.${e.tableName}`,
                    })}
                    <div className="font-mono text-[11px] text-muted">{e.recordId}</div>
                  </td>
                  <td>
                    <Badge
                      tone={
                        e.action === 'DELETE' ? 'off' : e.action === 'INSERT' ? 'ok' : 'neutral'
                      }
                    >
                      {t(`audit.actions.${e.action}`)}
                    </Badge>
                  </td>
                  <td>
                    <Changes entry={e} />
                  </td>
                </tr>
              ))}
            </Table>
            <Pager offset={offset} count={log.data.length} onChange={setOffset} />
          </>
        ))}

      {tab === 'security' &&
        canEvents &&
        (events.isPending ? (
          <Loading />
        ) : events.isError ? (
          <ErrorBox error={events.error} />
        ) : (
          <>
            <Table head={[t('audit.when'), t('audit.event'), t('audit.who'), t('audit.ip')]}>
              {events.data.length === 0 && <EmptyRow colSpan={4}>{t('audit.empty')}</EmptyRow>}
              {events.data.map((e) => (
                <tr key={e.id}>
                  <td className="whitespace-nowrap font-mono text-xs">
                    {dateTime.format(new Date(e.occurredAt))}
                  </td>
                  <td className="text-xs">
                    {t(`audit.events.${e.kind}`, { defaultValue: e.kind })}
                    {typeof e.details.reason === 'string' && (
                      <div className="text-muted">{e.details.reason}</div>
                    )}
                    {e.kind === 'data_export' && typeof e.details.screen === 'string' && (
                      <div className="text-muted">
                        {t('audit.exportDetail', {
                          screen: e.details.screen,
                          rows: Number(e.details.rows),
                        })}
                      </div>
                    )}
                  </td>
                  <td className="text-xs">{e.userId ? nameOf(e.userId) : (e.email ?? '—')}</td>
                  <td className="font-mono text-xs">{e.clientIp ?? '—'}</td>
                </tr>
              ))}
            </Table>
            <Pager offset={offset} count={events.data.length} onChange={setOffset} />
          </>
        ))}
    </>
  );
}
